import { useState, useRef, useLayoutEffect } from 'react';
import { Flashcard as FlashcardType, Rating } from '../services/api';
import {
  Flashcard as DSFlashcard,
  RatingButton,
  RatingZone,
  Rating as DesignRating,
  Button,
  Badge,
  CardSize,
  CardInfoBox,
} from '@hyggecards/design-system';
import MarkdownContent from './MarkdownContent';
import { useSound } from '../contexts/SoundContext';
import './Flashcard.css';

interface FlashcardProps {
  flashcard: FlashcardType;
  onRate: (rating: Rating) => void;
  showInfo: boolean;
}

// Map design system rating to API rating
const designToApiRating: Record<DesignRating, Rating> = {
  hard: '1',
  medium: '2',
  easy: '3',
  skip: 'pass',
};

// Threshold for switching to large card (in pixels)
const CONTENT_OVERFLOW_THRESHOLD = 280;

function Flashcard({ flashcard, onRate, showInfo }: FlashcardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [cardSize, setCardSize] = useState<CardSize>('standard');
  const questionRef = useRef<HTMLDivElement>(null);
  const answerRef = useRef<HTMLDivElement>(null);
  const { play } = useSound();

  // Measure content and determine if we need large card
  useLayoutEffect(() => {
    const questionHeight = questionRef.current?.scrollHeight ?? 0;
    const answerHeight = answerRef.current?.scrollHeight ?? 0;
    const maxContentHeight = Math.max(questionHeight, answerHeight);

    console.log('[Flashcard] Content measurement:', {
      questionHeight,
      answerHeight,
      maxContentHeight,
      threshold: CONTENT_OVERFLOW_THRESHOLD,
      willBeLarge: maxContentHeight > CONTENT_OVERFLOW_THRESHOLD,
    });

    // Switch to large if either question or answer would overflow
    if (maxContentHeight > CONTENT_OVERFLOW_THRESHOLD) {
      setCardSize('large');
    } else {
      setCardSize('standard');
    }
  }, [flashcard.id, flashcard.question, flashcard.answer]);

  const handleFlip = () => {
    play('flip');
    setIsFlipped(!isFlipped);
  };

  const handleDesignRating = (rating: DesignRating) => {
    // Check if card improved (went from lower to higher rating)
    const previousRating = flashcard.rating;
    const ratingValues: Record<string, number> = { '1': 1, '2': 2, '3': 3 };
    const newRatingValue = ratingValues[designToApiRating[rating]] ?? 0;
    const prevRatingValue = previousRating ? (ratingValues[previousRating] ?? 0) : 0;

    if (prevRatingValue > 0 && newRatingValue > prevRatingValue) {
      // Card improved - play the improved sound
      play('improved');
    } else {
      // Normal rating sound
      play(rating);
    }

    const apiRating = designToApiRating[rating];
    onRate(apiRating);
    setIsFlipped(false);
  };

  const handleHide = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRate('hide');
    setIsFlipped(false);
  };

  return (
    <div
      className={`flashcard-container ${cardSize === 'large' ? 'flashcard-container--large' : ''} ${!isFlipped ? 'flashcard-container--can-skip' : ''}`}
    >
      {/* Hidden measurement divs - used to calculate content height */}
      <div className="flashcard-measure" aria-hidden="true">
        <div ref={questionRef} className="flashcard-measure-content">
          <MarkdownContent content={flashcard.question} />
        </div>
        <div ref={answerRef} className="flashcard-measure-content">
          <MarkdownContent content={flashcard.answer} />
        </div>
      </div>

      {/* Desktop skip zone - right side of card */}
      {!isFlipped && (
        <button
          className="skip-zone"
          onClick={() => handleDesignRating('skip')}
          aria-label="Skip this card"
        >
          <span className="skip-zone__label">Skip</span>
        </button>
      )}

      {/* Desktop rating zones - right side of card (when flipped) */}
      <div className={`rating-zones ${!isFlipped ? 'rating-zones--hidden' : ''}`}>
        <RatingZone rating="hard" onClick={() => handleDesignRating('hard')} />
        <RatingZone rating="medium" onClick={() => handleDesignRating('medium')} />
        <RatingZone rating="easy" onClick={() => handleDesignRating('easy')} />
      </div>

      <DSFlashcard
        question={
          <div className="flashcard-question-content">
            <h2>Question</h2>
            <MarkdownContent content={flashcard.question} />
          </div>
        }
        answer={
          <div className="flashcard-answer-content">
            <h2>Answer</h2>
            <MarkdownContent content={flashcard.answer} />
          </div>
        }
        size={cardSize}
        flipped={isFlipped}
        onFlip={handleFlip}
      />

      {/* Unified action zone - all card actions in one place */}
      <div className="action-zone">
        {/* Primary actions: Rating buttons (visible when flipped) */}
        <div className={`primary-actions ${!isFlipped ? 'primary-actions--hidden' : ''}`}>
          <RatingButton rating="hard" onClick={() => handleDesignRating('hard')} showLabel />
          <RatingButton rating="medium" onClick={() => handleDesignRating('medium')} showLabel />
          <RatingButton rating="easy" onClick={() => handleDesignRating('easy')} showLabel />
        </div>

        {/* Secondary action: Skip (visible when not flipped) */}
        <div className={`secondary-actions ${isFlipped ? 'secondary-actions--hidden' : ''}`}>
          <RatingButton rating="skip" onClick={() => handleDesignRating('skip')} showLabel />
        </div>

        {/* Tertiary action: Don't show again (always available, subtle) */}
        <div className="tertiary-actions">
          <Button variant="ghost" size="sm" onClick={handleHide}>
            Don't show again
          </Button>
        </div>
      </div>

      {showInfo && (
        <CardInfoBox>
          {flashcard.selectionDetail && (
            <Badge
              variant={
                `selection-${flashcard.selectionReason || 'due'}` as
                  | 'selection-due'
                  | 'selection-semantic'
                  | 'selection-new'
                  | 'selection-reinforcement'
              }
            >
              {flashcard.selectionDetail}
            </Badge>
          )}
          {flashcard.contentType === 'curated' && <Badge variant="curated">Built-in</Badge>}
          {flashcard.deck && <Badge variant="deck">{flashcard.deck}</Badge>}
          {flashcard.isAIGenerated && <Badge variant="ai">AI Generated</Badge>}
          {flashcard.sourceFile && (
            <span className="stc-card-info-box__source">From: {flashcard.sourceFile}</span>
          )}
        </CardInfoBox>
      )}
    </div>
  );
}

export default Flashcard;
